import { play, stop } from '../core/audio'
import { clear, h, isAbort, wait } from '../core/dom'
import { haptic } from '../core/haptics'
import { back, go } from '../core/router'
import { getSettings, recordRescueSession } from '../core/store'
import { shutUp, speak } from '../core/voice'
import { MANTRAS, mantraText } from '../data/mantras'
import { getLang, num, t } from '../i18n'
import { topbar } from './shared'

/**
 * 3AM mode.
 *
 * This is the screen the app exists for. Everything else is a bedtime routine;
 * this is for the moment someone surfaces at 03:40 with their heart going and
 * no idea what is happening to them.
 *
 * Three decisions shape it:
 *
 *  - It never tries to get you back to sleep. Chasing sleep at 3am is what turns
 *    one bad hour into five. The stated goal is surviving the hour.
 *  - It is linear. No menus, no choices — one screen at a time, one instruction
 *    each. Decision-making is the first thing withdrawal takes away.
 *  - The red-flag exit is on the first screen, not buried at the end. Someone
 *    having a seizure should not have to tap through a breathing exercise.
 */
export function renderNight3am(root: HTMLElement, signal: AbortSignal): void {
  recordRescueSession()
  document.documentElement.dataset.mode = 'rescue'

  signal.addEventListener(
    'abort',
    () => {
      delete document.documentElement.dataset.mode
      shutUp()
      stop()
    },
    { once: true },
  )

  let step = 0
  const TOTAL = 5

  const paint = (): void => {
    clear(root)
    const d = t().night3am

    const nav = h(
      'div',
      { class: 'stack-sm' },
      h(
        'button',
        {
          class: 'btn warm block',
          onclick: () => {
            haptic.tap()
            step++
            if (step >= TOTAL) return done()
            paint()
          },
        },
        step === TOTAL - 1 ? t().common.done : t().common.next,
      ),
      step === 0 &&
        h(
          'button',
          {
            class: 'btn danger block',
            onclick: () => {
              haptic.warn()
              go('safety')
            },
          },
          d.emergency,
        ),
    )

    const header = h(
      'div',
      { class: 'stack-sm' },
      h('div', { class: 'rescue-count' }, d.step(step + 1, TOTAL)),
    )

    switch (step) {
      case 0:
        root.append(
          topbar(),
          h(
            'div',
            { class: 'rescue-step fade-in' },
            header,
            h('h1', {}, d.title),
            h('p', {}, d.intro),
            h('div', { class: 'divider' }),
            h('h1', { style: { fontSize: '1.2rem' } }, d.s1Title),
            h('p', {}, d.s1Body),
            h(
              'div',
              { class: 'notice' },
              h('h3', {}, d.redFlagTitle),
              h('p', {}, d.redFlagBody),
            ),
          ),
          nav,
        )
        void speak(d.s1Title, { rate: 0.7 })
        break

      case 1:
        root.append(
          topbar(),
          h(
            'div',
            { class: 'rescue-step fade-in' },
            header,
            h('h1', {}, d.s2Title),
            h('p', {}, d.s2Body),
          ),
          h('div', { style: { paddingBottom: '18px' } }, breathCircle(signal)),
          nav,
        )
        break

      case 2:
        root.append(
          topbar(),
          h(
            'div',
            { class: 'rescue-step fade-in' },
            header,
            h('h1', {}, d.s3Title),
            h('p', {}, d.s3Body),
            h('div', { class: 'stack-sm' }, ...countdownChips()),
          ),
          nav,
        )
        void speak(d.s3Body, { rate: 0.68 })
        break

      case 3: {
        // Mantras marked 'panic' are the only ones written to hold up when the
        // body has already decided something is wrong.
        const pool = MANTRAS.filter((m) => m.mood === 'panic' || m.mood === 'withdrawal')
        const custom = getSettings().customMantra.trim()
        const line = custom || mantraText(pool[Math.floor(Math.random() * pool.length)]!, getLang())

        root.append(
          topbar(),
          h(
            'div',
            { class: 'rescue-step fade-in' },
            header,
            h('h1', {}, d.s4Title),
            h('p', {}, d.s4Body),
            h(
              'div',
              { class: 'mantra-text', style: { fontSize: '1.6rem', textAlign: 'center' } },
              line,
            ),
          ),
          nav,
        )
        void repeatLine(line, signal)
        break
      }

      default:
        root.append(
          topbar(),
          h(
            'div',
            { class: 'rescue-step fade-in' },
            header,
            h('h1', {}, d.s5Title),
            h('p', {}, d.s5Body),
          ),
          nav,
        )
        void speak(d.s5Body, { rate: 0.66 })
    }
  }

  const done = (): void => {
    const d = t().night3am
    clear(root)
    // Sound continues past the end of the sequence, fading on its own. Silence
    // at the moment someone puts the phone down is a jolt.
    void play('brown', getSettings().soundVolume * 0.5)

    root.append(
      topbar(),
      h(
        'div',
        { class: 'rescue-step fade-in center' },
        h('div', { style: { fontSize: '2.6rem' } }, '🌒'),
        h('p', { class: 'lede' }, d.logged),
      ),
      h(
        'div',
        { class: 'stack-sm' },
        h(
          'button',
          {
            class: 'btn warm block',
            onclick: () => {
              haptic.tap()
              step = 1
              paint()
            },
          },
          t().home.breath,
        ),
        h(
          'button',
          {
            class: 'btn ghost block',
            onclick: () => {
              haptic.tap()
              back()
            },
          },
          t().common.close,
        ),
      ),
    )
    haptic.complete()
  }

  paint()
}

/** A bare 4-in / 8-out pacer. No pattern picker — choosing is not free at 3am. */
function breathCircle(signal: AbortSignal): HTMLElement {
  const orb = h('div', {
    class: 'orb',
    style: { transform: 'scale(1)', width: '90px', position: 'relative', margin: '0 auto' },
  })
  const label = h('div', { class: 'breath-phase center' })
  const wrap = h('div', { class: 'stack-sm' }, orb, label)

  void (async () => {
    const d = t().breath
    try {
      for (;;) {
        label.textContent = d.inhale
        orb.style.transitionDuration = '4s'
        orb.style.transform = 'scale(1.9)'
        haptic.inhale()
        await wait(4000, signal)

        label.textContent = d.exhale
        orb.style.transitionDuration = '8s'
        orb.style.transform = 'scale(1)'
        haptic.exhale()
        await wait(8000, signal)
      }
    } catch (err) {
      if (!isAbort(err)) throw err
    }
  })()

  return wrap
}

/**
 * 5-4-3 grounding as tappable tallies.
 *
 * The point is not the counting — it is having a physical target to hit twelve
 * times, so the hands are busy and the count is external instead of one more
 * thing to hold in a head that is already failing to hold things.
 */
function countdownChips(): HTMLElement[] {
  const groups: Array<[string, number]> = [
    ['👁', 5],
    ['👂', 4],
    ['✋', 3],
  ]
  return groups.map(([glyph, count]) =>
    h(
      'div',
      { class: 'chips', style: { width: '100%' } },
      h('span', { class: 'faint', style: { alignSelf: 'center', minWidth: '26px' } }, glyph),
      ...Array.from({ length: count }, (_, i) => {
        const chip = h(
          'button',
          {
            class: 'chip',
            onclick: () => {
              haptic.tap()
              chip.classList.toggle('on')
            },
          },
          num(i + 1),
        )
        return chip
      }),
    ),
  )
}

async function repeatLine(line: string, signal: AbortSignal): Promise<void> {
  try {
    for (;;) {
      void speak(line, { rate: 0.68, pitch: 0.85 })
      await wait(9000, signal)
    }
  } catch (err) {
    if (!isAbort(err)) throw err
  }
}
